import { isDesktopShell } from "../../common/hooks/useIsDesktopView";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { IconUserCircle } from "@tabler/icons-react";
import {
  useConsumeLocationState,
  useLocationState,
} from "../../domains/app/hooks/useLocationState";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import ProfilePicture from "../../components/ProfilePicture";
import {
  GPTHistoryButton,
  GPTHistoryScrim,
  useGptHistoryReveal,
} from "../../components/gpt/GPTHistoryPanel";
import { GPTGreeting } from "../../components/gpt/GPTGreeting";
import {
  GPTConversationWrapper,
  GPT_HEADER_ROW,
} from "../../components/gpt/layout";
import { ChatMessageCard } from "../../components/ui/ChatMessageCard";
import { ChatLoader } from "../../components/ui/ChatLoader";
import { ChatInput } from "../../components/ui/ChatInput";
import { useDictationControl } from "../../domains/gpt/hooks/useDictationControl";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { iosGenerateHaptic } from "../../tools/ios";
import { HapticEffect } from "../../interfaces";
import { displayToast } from "../../components/app/AppToast";
import { cn } from "../../lib/utils";
import {
  useEvidenceGptChat,
  type EvidenceSourceView,
} from "../../domains/gpt/hooks/useEvidenceGptChat";
import { EvidenceAnswer } from "../../components/gpt/evidence/EvidenceAnswer";
import { EvidenceSourcesList } from "../../components/gpt/evidence/EvidenceSourcesList";
import { EvidenceCitationSheet } from "../../components/gpt/evidence/EvidenceCitationSheet";
import { EvidenceHistoryDrawer } from "../../components/gpt/evidence/EvidenceHistoryDrawer";

// A prefilled question handed over from another surface (e.g. the "Ask
// JuisciGPT" deep-link on SearchKeyFacts) — the same contract the streaming
// new-chat page reads. Only consumed for a FRESH thread; a hydrated
// conversation ignores it.
const locationStateSchema = z.object({
  initialQuestion: z.string().nullable().default(null),
});

/**
 * The Evidence GPT conversation surface — the webapp's default agent behind the
 * EXPERIMENTAL flag. Blocking turns (no stream): the answer arrives whole, so a
 * pending turn shows the question + a loader while `POST /turn` runs. A fresh
 * thread (`conversationId: null`) mints its id on the first turn, then navigates
 * to the real url so a reload/back finds it.
 */
export function EvidenceChatSurface({
  conversationId,
  roomId,
}: {
  conversationId: string | null;
  roomId: string | null;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { gptConversationPageLink, gptNewChatPageLink } = usePageLinks(roomId);
  const { scrollRef, scrollToBottom } = useChatScrollAnchor({
    initialToBottom: conversationId !== null,
  });
  const { initialQuestion } = useLocationState(locationStateSchema);
  const consumeLocationState = useConsumeLocationState();
  const [inputQuestion, setInputQuestion] = useState("");
  // Voice dictation — the same composer aid the general JuisciGPT input has.
  // Purely client-side: it transcribes into the composer text, which is then
  // sent exactly like a typed question, so no `/turn` change is needed.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const dictation = useDictationControl({
    onTranscript: setInputQuestion,
    textareaRef: composerRef,
    onSend: () => {
      runSend(inputQuestion);
    },
    canSend: () => inputQuestion.trim() !== "" && !chat.isSending,
  });
  const [openSource, setOpenSource] = useState<{
    source: EvidenceSourceView;
    citationNumber: number;
    quotes: readonly string[];
  } | null>(null);
  // The conversation-history drawer (burger, top-left) — same push-aside reveal
  // the general chat uses.
  // This legacy surface brings its own drawer, so it sits outside the general
  // conversation page's card and plays no arrival animation: a row that opens
  // an Evidence thread cuts to it (see `gptHistoryArrivalSchema`).
  const historyReveal = useGptHistoryReveal({
    arrival: "closed",
    swipeEnabled: true,
  });
  // One greeting draw per arrival on this surface (see GPTGreeting).
  const greetingSeed = useMemo(() => Math.random(), []);
  const sending = useSingleFlightAction();

  // Evidence GPT defaults to web-augmented retrieval (docs §8). The per-turn
  // toggle is not exposed on the conversational surface — the default is on.
  const chat = useEvidenceGptChat({
    conversationId,
    includeWeb: true,
    language: currentUser === null ? "en" : currentUser.language,
    // The standalone Evidence surface owns its own history hydration.
    hydrate: true,
  });

  function runSend(question: string): void {
    const trimmed = question.trim();
    if (trimmed === "" || chat.isSending) return;
    dictation.stop();
    iosGenerateHaptic(HapticEffect.SUCCESS);
    setInputQuestion("");
    void sending.run(async () => {
      // The standalone Evidence surface is a forced-Evidence send — no router.
      const result = await chat.send(trimmed, "typed", null);
      if (result === null) {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      // First turn on a fresh thread: move to the real url (no state — the
      // target hydrates the just-persisted turn). Follow-ups stay in place.
      if (result.created)
        history.replace(gptConversationPageLink(result.conversationId));
      scrollToBottom();
    });
  }

  // Auto-ask the handed-over question ONCE, on a fresh thread only. Mirrors the
  // streaming new-chat page's initialQuestion effect so the "Ask JuisciGPT"
  // deep-link isn't silently dropped when EXPERIMENTAL routes here instead.
  // Empty deps: the handoff is a one-shot read of the arrival router state —
  // and consumed before it is acted on, so a restore of this entry (reload,
  // browser reopen) cannot replay the send into a second conversation.
  useEffect(() => {
    if (conversationId === null && initialQuestion !== null) {
      consumeLocationState();
      runSend(initialQuestion);
    }
  }, []);

  const hasTurns = chat.turns.length > 0;

  return (
    <>
      <EvidenceHistoryDrawer
        isOpen={historyReveal.isOpen}
        roomId={roomId}
        onNewConversation={() => {
          historyReveal.close();
          history.push(gptNewChatPageLink());
        }}
      />
      <ScrollableLayout
        background="var(--color-ds-cream)"
        containerClassName={historyReveal.containerClassName}
        containerStyle={historyReveal.containerStyle}
        style={historyReveal.scrollLockStyle}
        headerClassName="bg-ds-cream"
        header={
          <div className={cn(GPT_HEADER_ROW, "justify-between")}>
            <GPTHistoryButton className={null} onClick={historyReveal.open} />
            {currentUser === null ? (
              <Link to="/login">
                <IconUserCircle size={26} className="text-primary-black" />
              </Link>
            ) : (
              <Link to="/profile">
                <ProfilePicture user={currentUser} height={32} badge={null} />
              </Link>
            )}
          </div>
        }
        scrollRef={scrollRef}
      >
        <GPTHistoryScrim
          isOpen={historyReveal.isOpen}
          onClose={historyReveal.close}
        />
        <div className="page-wrapper">
          <GPTConversationWrapper className="bg-ds-cream pt-4 pb-[calc(120px+var(--safe-area-bottom))]">
            {chat.isHydrating ? (
              <ChatLoader variant="cwyop" />
            ) : hasTurns ? (
              <div className="flex flex-col gap-6">
                {chat.turns.map((turn, index) => (
                  <div key={index} className="flex flex-col gap-3">
                    <ChatMessageCard
                      variant={isDesktopShell() ? "desktop" : "default"}
                      className="mx-5"
                    >
                      {turn.question}
                    </ChatMessageCard>
                    {turn.status === "answered" ? (
                      <div className="flex flex-col gap-3 px-5">
                        <EvidenceAnswer
                          answer={turn.answer}
                          onOpenSource={(source, citationNumber, quotes) => {
                            setOpenSource({ source, citationNumber, quotes });
                          }}
                        />
                        {/* This legacy surface has no Sources tab, so the
                            source list stays inline under the answer. */}
                        <EvidenceSourcesList
                          answer={turn.answer}
                          onOpenSource={(source, citationNumber, quotes) => {
                            setOpenSource({ source, citationNumber, quotes });
                          }}
                        />
                      </div>
                    ) : turn.status === "pending" ? (
                      <ChatLoader variant="cwyop" />
                    ) : (
                      <p className="mx-5 text-sm text-secondary-raspberry-1">
                        {t("❌ Sorry an error has occurred. Please retry.")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Reuse the general JuisciGPT landing greeting — no separate
              // "Evidence GPT" branding. Centered in the free space above the composer.
              <div className="flex min-h-[60dvh] flex-1 flex-col items-center justify-center px-5">
                <GPTGreeting seed={greetingSeed} size="mobile" />
              </div>
            )}
          </GPTConversationWrapper>
        </div>

        <KeyboardAvoidingView>
          <div className="fixed inset-x-0 bottom-0 px-5 pb-[calc(16px+var(--safe-area-bottom))]">
            <ChatInput
              ref={composerRef}
              className="w-full"
              placeholder={t("Ask a medical question...")}
              value={inputQuestion}
              readOnly={dictation.composerReadOnly}
              onFocus={() => {
                if (dictation.isActive) dictation.stop();
              }}
              onChange={(e) => {
                setInputQuestion(e.target.value);
              }}
              mode={null}
              leading={null}
              leadingPanel={null}
              banner={null}
              attach={null}
              dictation={dictation.composer}
              stream={null}
              onSend={dictation.send}
              sendDisabled={chat.isSending}
            />
          </div>
        </KeyboardAvoidingView>

        <EvidenceCitationSheet
          source={openSource === null ? null : openSource.source}
          citationNumber={
            openSource === null ? null : openSource.citationNumber
          }
          quotes={openSource === null ? [] : openSource.quotes}
          roomId={roomId}
          isOpen={openSource !== null}
          onClose={() => {
            setOpenSource(null);
          }}
        />
      </ScrollableLayout>
    </>
  );
}
